/**
 * Reviews behaviour for sections/dorahi-reviews.liquid.
 * Port of dorahi-main/components/reviews/* + lib/judgeme.
 *
 * Fetches the Judge.me widget API directly (public token) and parses the
 * widget HTML with DOMParser — same fields the app's
 * parseWidgetHtmlForReviews pulled out with regexes. Renders the photo strip,
 * masonry cards, sort dropdown, load-more, image lightbox and the write-review
 * modal (posts to Judge.me's public create endpoint, like lib/judgeme
 * createReview).
 */
(function () {
  const JUDGEME_API_BASE = "https://judge.me/api/v1";
  // The widget's own submit host (CORS `*`). Photo uploads follow the same
  // 3-step flow Judge.me's form.js uses: presigned S3 form -> browser puts the
  // file on S3 -> review POST carries the S3 keys as picture_keys[n].
  const JUDGEME_SUBMIT_BASE = "https://api.judge.me/api/v1";
  const CARD_MIN_HEIGHTS = [
    200, 260, 220, 300, 190, 250, 280, 200, 230, 310, 210, 265,
  ];
  const SORT_OPTIONS = [
    { value: "image_first", label: "Image First" },
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "highest", label: "Highest Rated" },
    { value: "lowest", label: "Lowest Rated" },
  ];
  const RATING_LABELS = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent",
  };

  const escapeHtml = (text) =>
    String(text ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );

  const ensureHttps = (url) =>
    url && url.startsWith("//") ? `https:${url}` : url || "";

  // lib/judgeme parseWidgetHtmlForReviews formats widget dates absolutely
  // (its relative formatReviewDate only runs on the JSON API path), so match
  // that — otherwise these read "5 months ago" where the live site shows a date.
  function formatReviewDate(dateString) {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString || "";
    // Judge.me stamps these in UTC ('2026-04-04 18:59:19 UTC'); render in UTC
    // too, otherwise late-evening reviews shift to the next day in IST.
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  }

  // star-rating.tsx (non-interactive) as an HTML string
  function starRatingHtml(rating, sizePx) {
    const size = sizePx || 18;
    let html = "";
    for (let i = 0; i < 5; i++) {
      const filled = i < Math.floor(rating);
      const partial = !filled && i < rating;
      const pct = partial ? (rating - i) * 100 : 0;
      html += `
        <span class="relative inline-flex" style="width:${size}px;height:${size}px">
          <svg viewBox="0 0 24 24" fill="currentColor" class="text-gray-300 absolute inset-0" width="${size}" height="${size}" aria-hidden="true"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          ${
            filled || partial
              ? `<span class="absolute inset-0 overflow-hidden" style="width:${filled ? 100 : pct}%"><svg viewBox="0 0 24 24" fill="currentColor" class="text-dorahi-orange" width="${size}" height="${size}" aria-hidden="true"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></span>`
              : ""
          }
        </span>`;
    }
    return html;
  }

  function parseWidgetHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");

    const widget = doc.querySelector("[data-average-rating]");
    const averageRating = widget
      ? parseFloat(widget.getAttribute("data-average-rating")) || 0
      : 0;
    const countEl = doc.querySelector("[data-number-of-reviews]");
    const totalReviews = countEl
      ? parseInt(countEl.getAttribute("data-number-of-reviews"), 10) || 0
      : 0;

    const reviews = [];
    doc.querySelectorAll("[data-review-id]").forEach((node) => {
      const rating =
        parseInt(
          node.querySelector("[data-score]")?.getAttribute("data-score") || "",
          10,
        ) ||
        node.querySelectorAll(".jdgm-star--on").length ||
        5;

      const reviewerName =
        (
          node.querySelector(".jdgm-rev__author")?.textContent || "Customer"
        ).trim() || "Customer";
      const title = (
        node.querySelector(".jdgm-rev__title")?.textContent || ""
      ).trim();
      const body = (
        node.querySelector(".jdgm-rev__body")?.textContent || ""
      ).trim();

      const timestamp =
        node
          .querySelector(".jdgm-rev__timestamp")
          ?.getAttribute("data-content") ||
        node.querySelector("[data-content]")?.getAttribute("data-content") ||
        "";

      // Same substring test lib/judgeme's parseWidgetHtmlForReviews uses. Note
      // the third check also matches `jdgm-rev__buyer-badge-wrapper`, which
      // Judge.me emits for every review — that's why the live site shows
      // "Verified" on all of them, so keep the behaviour identical here.
      const blockHtml = node.outerHTML;
      const isVerified =
        blockHtml.includes("data-verified-buyer='true'") ||
        blockHtml.includes('data-verified-buyer="true"') ||
        blockHtml.includes("jdgm-rev__buyer-badge");

      // Collect judge.me-hosted images; prefer high-res links when present.
      const pictures = [];
      const seen = new Set();
      node.querySelectorAll("img, a").forEach((el) => {
        const candidates = [
          el.getAttribute("data-fancybox-src"),
          el.getAttribute("data-huge-src"),
          el.getAttribute("href"),
          el.getAttribute("data-src"),
          el.getAttribute("src"),
        ].filter(Boolean);
        const url = candidates.find(
          (u) =>
            /judge\.me|judgeme|amazonaws\.com\/judgeme/.test(u) &&
            !u.includes("placeholder"),
        );
        if (!url) return;
        const full = ensureHttps(url);
        const filename = full.split("/").pop()?.split("?")[0] || full;
        if (seen.has(filename)) return;
        seen.add(filename);
        pictures.push({
          small: full.replace(/\/(huge|original)\//, "/compact/"),
          large: full.replace(/\/(compact|small|thumb)\//, "/huge/"),
        });
      });

      reviews.push({
        rating,
        reviewerName,
        reviewerInitial: (reviewerName.charAt(0) || "C").toUpperCase(),
        title,
        body,
        createdAt: timestamp,
        formattedDate: formatReviewDate(timestamp),
        isVerified,
        pictures,
        hasPhotos: pictures.length > 0,
      });
    });

    return { averageRating, totalReviews, reviews };
  }

  class DorahiReviews extends HTMLElement {
    connectedCallback() {
      this.visibleCount = 10;
      this.sortBy = "image_first";
      this.reviews = [];
      this.stats = { averageRating: 0, totalReviews: 0 };
      this.writePhotos = [];

      this.bindSort();
      this.bindLightbox();
      this.bindWriteModal();
      this.fetchReviews();
    }

    q(selector) {
      return this.querySelector(selector);
    }

    async fetchReviews() {
      const params = new URLSearchParams({
        api_token: this.dataset.apiToken,
        shop_domain: this.dataset.shopDomain,
        external_id: this.dataset.productId,
        page: "1",
        per_page: this.dataset.perPage || "50",
      });

      try {
        const response = await fetch(
          `${JUDGEME_API_BASE}/widgets/product_review?${params}`,
          {
            headers: { Accept: "application/json" },
          },
        );
        if (!response.ok) throw new Error(`Judge.me ${response.status}`);
        const text = await response.text();

        let html = text;
        try {
          const json = JSON.parse(text);
          html = json.widget || "";
        } catch (error) {
          /* raw widget HTML */
        }

        const data = parseWidgetHtml(html);
        this.reviews = data.reviews;
        this.stats = data;
        this.q("[data-reviews-skeleton]").classList.add("hidden");
        this.q("[data-reviews-content]").classList.remove("hidden");
        this.render();
      } catch (error) {
        console.warn("[dorahi-reviews] failed to load reviews:", error);
        this.q("[data-reviews-skeleton]").classList.add("hidden");
        this.q("[data-reviews-error]").classList.remove("hidden");
      }
    }

    sortedReviews() {
      const list = [...this.reviews];
      const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
      switch (this.sortBy) {
        case "image_first":
          return list.sort(
            (a, b) =>
              (b.hasPhotos ? 1 : 0) - (a.hasPhotos ? 1 : 0) || byNewest(a, b),
          );
        case "newest":
          return list.sort(byNewest);
        case "oldest":
          return list.sort((a, b) => byNewest(b, a));
        case "highest":
          return list.sort((a, b) => b.rating - a.rating);
        case "lowest":
          return list.sort((a, b) => a.rating - b.rating);
        default:
          return list;
      }
    }

    render() {
      const reviews = this.sortedReviews();
      this.currentList = reviews;

      // Header: stars + count
      const avg = this.stats.averageRating || 0;
      this.q("[data-reviews-stars]").innerHTML = starRatingHtml(avg, 18);
      const total = this.stats.totalReviews || reviews.length;
      this.q("[data-reviews-count]").textContent =
        `${total} ${total === 1 ? "Review" : "Reviews"}`;

      // product-reviews.tsx only renders the sort control when there are reviews.
      this.q("[data-sort-dropdown]")?.classList.toggle("hidden", total === 0);

      // Photo strip
      const allImages = [];
      reviews.forEach((review, reviewIndex) => {
        review.pictures.forEach((pic, imageIndex) => {
          allImages.push({ reviewIndex, imageIndex, src: pic.small });
        });
      });
      this.allImages = allImages;

      const stripWrap = this.q("[data-photo-strip-wrap]");
      const strip = this.q("[data-photo-strip]");
      stripWrap.classList.toggle("hidden", allImages.length === 0);
      strip.innerHTML = allImages
        .map(
          (img, idx) => `
            <button type="button" data-strip-index="${idx}" class="relative h-20 w-20 md:h-24 md:w-24 shrink-0 rounded-xl overflow-hidden hover:opacity-85 transition-opacity">
              <img src="${escapeHtml(img.src)}" alt="Customer photo ${idx + 1}" loading="lazy" class="absolute inset-0 w-full h-full object-cover">
            </button>`,
        )
        .join("");
      strip.querySelectorAll("[data-strip-index]").forEach((button) => {
        button.addEventListener("click", () => {
          const img = this.allImages[Number(button.dataset.stripIndex)];
          this.openLightbox(img.reviewIndex, img.imageIndex);
        });
      });

      // Masonry grid
      const grid = this.q("[data-reviews-grid]");
      grid.innerHTML = reviews
        .slice(0, this.visibleCount)
        .map((review, index) => this.cardHtml(review, index))
        .join("");

      grid.querySelectorAll("[data-card-image]").forEach((button) => {
        button.addEventListener("click", () => {
          this.openLightbox(Number(button.dataset.reviewIndex), 0);
        });
      });
      grid.querySelectorAll("[data-read-more]").forEach((button) => {
        button.addEventListener("click", () => {
          const review = reviews[Number(button.dataset.reviewIndex)];
          const bodyEl = button.previousElementSibling;
          const expanded = button.dataset.expanded === "true";
          button.dataset.expanded = expanded ? "false" : "true";
          bodyEl.textContent = expanded
            ? `${review.body.slice(0, 180)}…`
            : review.body;
          button.querySelector("[data-read-more-label]").textContent = expanded
            ? "Read more"
            : "Show less";
        });
      });

      // Load more
      this.q("[data-load-more-wrap]").classList.toggle(
        "hidden",
        this.visibleCount >= reviews.length,
      );
      const loadMore = this.q("[data-load-more]");
      if (!loadMore.dataset.bound) {
        loadMore.dataset.bound = "true";
        loadMore.addEventListener("click", () => {
          this.visibleCount += 10;
          this.render();
        });
      }
    }

    // review-card.tsx
    cardHtml(review, index) {
      const minHeight = CARD_MIN_HEIGHTS[index % CARD_MIN_HEIGHTS.length];
      const truncated = review.body.length > 180;
      const displayText = truncated
        ? `${review.body.slice(0, 180)}…`
        : review.body;

      return `
        <div class="break-inside-avoid mb-4 bg-white overflow-hidden flex flex-col rounded-[30px] mobile:rounded-[25px] rounded-squircle border border-[#E5E7EB]" style="min-height:${minHeight}px">
          ${
            review.pictures.length
              ? `<button type="button" data-card-image data-review-index="${index}" class="relative w-full aspect-4/3 block overflow-hidden">
                  <img src="${escapeHtml(review.pictures[0].large)}" alt="Photo by ${escapeHtml(review.reviewerName)}" loading="lazy" class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105">
                  ${
                    review.pictures.length > 1
                      ? `<span class="absolute bottom-2 right-2 bg-black/55 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">+${review.pictures.length - 1}</span>`
                      : ""
                  }
                </button>`
              : ""
          }
          <div class="p-4 md:p-5 flex flex-col flex-1">
            <div class="flex items-center justify-between gap-1 mb-1">
              <div class="flex items-center gap-2.5 min-w-0">
                <span class="font-normal text-sm text-gray-500 truncate">${escapeHtml(review.reviewerName)}</span>
              </div>
              ${
                review.isVerified
                  ? `<div class="flex items-center gap-0.5 text-[10px] text-green-600 shrink-0">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
                      <span>Verified</span>
                    </div>`
                  : ""
              }
            </div>
            <div class="flex items-center gap-2 mb-2"><span class="flex items-center gap-1">${starRatingHtml(review.rating, 14)}</span></div>
            ${review.title ? `<p class="font-semibold text-sm text-gray-900 mb-1.5 leading-snug">${escapeHtml(review.title)}</p>` : ""}
            <p class="text-sm text-gray-600 leading-relaxed flex-1">${escapeHtml(displayText)}</p>
            ${
              truncated
                ? `<button type="button" data-read-more data-review-index="${index}" data-expanded="false" class="mt-2 text-xs text-dorahi-pink font-medium hover:underline flex items-center gap-0.5">
                    <span data-read-more-label>Read more</span>
                  </button>`
                : ""
            }
            <div class="mt-3 pt-2 border-t border-gray-100">
              <span class="text-[11px] text-gray-400">${escapeHtml(review.formattedDate)}</span>
            </div>
          </div>
        </div>`;
    }

    /* ------------- sort dropdown ------------- */

    bindSort() {
      const toggle = this.q("[data-sort-toggle]");
      const menu = this.q("[data-sort-menu]");
      const menuInner = menu.firstElementChild;

      menuInner.innerHTML = SORT_OPTIONS.map(
        (option) =>
          `<button type="button" data-sort-value="${option.value}" class="w-full text-left px-4 py-3 text-sm hover:bg-pink-50 transition-colors">${option.label}</button>`,
      ).join("");

      const paint = () => {
        this.q("[data-sort-label]").textContent = SORT_OPTIONS.find(
          (o) => o.value === this.sortBy,
        ).label;
        menuInner.querySelectorAll("[data-sort-value]").forEach((button) => {
          const active = button.dataset.sortValue === this.sortBy;
          button.classList.toggle("text-dorahi-pink", active);
          button.classList.toggle("font-medium", active);
          button.classList.toggle("bg-pink-50", active);
        });
      };
      paint();

      toggle.addEventListener("click", (e) => {
        e.stopPropagation();
        menu.classList.toggle("hidden");
        toggle
          .querySelector("svg")
          ?.classList.toggle("rotate-180", !menu.classList.contains("hidden"));
      });
      menuInner.addEventListener("click", (e) => {
        const button = e.target.closest("[data-sort-value]");
        if (!button) return;
        this.sortBy = button.dataset.sortValue;
        this.visibleCount = 10;
        menu.classList.add("hidden");
        paint();
        this.render();
      });
      document.addEventListener("click", () => menu.classList.add("hidden"));
    }

    /* ------------- lightbox (review-lightbox.tsx) ------------- */

    bindLightbox() {
      const modal = this.q("[data-review-lightbox]");
      modal
        .querySelector("[data-review-lightbox-close]")
        .addEventListener("click", () => this.closeLightbox());
      modal
        .querySelector("[data-review-lightbox-backdrop]")
        .addEventListener("click", () => this.closeLightbox());
      modal
        .querySelector("[data-review-lightbox-prev]")
        .addEventListener("click", () => this.stepLightbox(-1));
      modal
        .querySelector("[data-review-lightbox-next]")
        .addEventListener("click", () => this.stepLightbox(1));
      document.addEventListener("keydown", (e) => {
        if (modal.classList.contains("hidden")) return;
        if (e.key === "Escape") this.closeLightbox();
        if (e.key === "ArrowLeft") this.stepLightbox(-1);
        if (e.key === "ArrowRight") this.stepLightbox(1);
      });
    }

    openLightbox(reviewIndex, imageIndex) {
      this.lightboxReviewIndex = reviewIndex;
      this.lightboxImageIndex = imageIndex;
      this.q("[data-review-lightbox]").classList.remove("hidden");
      document.body.style.overflow = "hidden";
      this.paintLightbox();
    }

    closeLightbox() {
      this.q("[data-review-lightbox]").classList.add("hidden");
      document.body.style.overflow = "";
    }

    stepLightbox(delta) {
      const globalIndex = this.allImages.findIndex(
        (img) =>
          img.reviewIndex === this.lightboxReviewIndex &&
          img.imageIndex === this.lightboxImageIndex,
      );
      const next = this.allImages[globalIndex + delta];
      if (!next) return;
      this.lightboxReviewIndex = next.reviewIndex;
      this.lightboxImageIndex = next.imageIndex;
      this.paintLightbox();
    }

    paintLightbox() {
      const review = this.currentList[this.lightboxReviewIndex];
      const picture = review?.pictures?.[this.lightboxImageIndex];
      if (!review || !picture) return;

      const globalIndex = this.allImages.findIndex(
        (img) =>
          img.reviewIndex === this.lightboxReviewIndex &&
          img.imageIndex === this.lightboxImageIndex,
      );

      this.q("[data-review-lightbox-image]").src = picture.large;
      this.q("[data-review-lightbox-counter]").textContent =
        `${globalIndex + 1} of ${this.allImages.length}`;
      this.q("[data-review-lightbox-initial]").textContent =
        review.reviewerInitial;
      this.q("[data-review-lightbox-name]").textContent = review.reviewerName;
      this.q("[data-review-lightbox-verified]").classList.toggle(
        "hidden",
        !review.isVerified,
      );
      this.q("[data-review-lightbox-stars]").innerHTML = starRatingHtml(
        review.rating,
        18,
      );
      this.q("[data-review-lightbox-date]").textContent = review.formattedDate;
      const titleEl = this.q("[data-review-lightbox-title]");
      titleEl.textContent = review.title;
      titleEl.classList.toggle("hidden", !review.title);
      this.q("[data-review-lightbox-body]").textContent = review.body;

      this.q("[data-review-lightbox-prev]").style.display =
        globalIndex > 0 ? "" : "none";
      this.q("[data-review-lightbox-next]").style.display =
        globalIndex < this.allImages.length - 1 ? "" : "none";

      const thumbsWrap = this.q("[data-review-lightbox-thumbs-wrap]");
      const thumbs = this.q("[data-review-lightbox-thumbs]");
      thumbsWrap.classList.toggle("hidden", review.pictures.length <= 1);
      thumbs.innerHTML = review.pictures
        .map(
          (pic, idx) => `
            <button type="button" data-thumb-index="${idx}" class="relative w-14 h-14 overflow-hidden transition-all rounded-[8px] rounded-squircle border ${
              idx === this.lightboxImageIndex
                ? "border-[#EE518A] ring-2 ring-dorahi-pink ring-offset-2"
                : "border-[#D1D5DB] opacity-70 hover:opacity-100"
            }">
              <img src="${escapeHtml(pic.small)}" alt="Thumbnail ${idx + 1}" class="absolute inset-0 w-full h-full object-cover">
            </button>`,
        )
        .join("");
      thumbs.querySelectorAll("[data-thumb-index]").forEach((button) => {
        button.addEventListener("click", () => {
          this.lightboxImageIndex = Number(button.dataset.thumbIndex);
          this.paintLightbox();
        });
      });
    }

    /* ------------- write review modal (write-review-modal.tsx) ------------- */

    // Photo upload, replicating Judge.me's own widget form: fetch a presigned
    // S3 POST, upload the file from the browser, return the S3 key that the
    // review create call references via picture_keys[n]. Throws on failure so
    // the submit handler surfaces it instead of silently dropping photos.
    async uploadReviewPhoto(file) {
      const params = new URLSearchParams({
        shop_domain: this.dataset.shopDomain,
        url: this.dataset.shopDomain,
        platform: "shopify",
      });
      const presignResponse = await fetch(
        `${JUDGEME_SUBMIT_BASE}/pictures/presigned_data?${params}`,
      );
      if (!presignResponse.ok)
        throw new Error("Could not upload photo. Please try again.");
      const presigned = await presignResponse.json();

      const safeName = (file.name || "photo.jpg").replace(/[^\w.-]+/g, "_");
      const key = `${presigned.key_prefix}${safeName}`;

      const uploadData = new FormData();
      uploadData.append("key", key);
      Object.entries(presigned.fields || {}).forEach(([field, value]) =>
        uploadData.append(field, value),
      );
      uploadData.append("file", file);

      const upload = await fetch(presigned.url, {
        method: "POST",
        body: uploadData,
      });
      if (!upload.ok)
        throw new Error("Could not upload photo. Please try again.");
      return key;
    }

    bindWriteModal() {
      const modal = this.q("[data-write-review-modal]");
      const form = this.q("[data-write-review-form]");
      const starsWrap = this.q("[data-write-review-stars]");
      const ratingLabel = this.q("[data-write-review-rating-label]");
      const fileInput = this.q("[data-write-review-file]");
      const previews = this.q("[data-write-review-previews]");
      const addButton = this.q("[data-write-review-add-photos]");
      const addLabel = this.q("[data-write-review-add-label]");
      const errorEl = this.q("[data-write-review-error]");

      this.writeRating = 0;

      // star-rating.tsx InteractiveStarRating: ONE star per button (size md =
      // 18px, gap-1 row) — starRatingHtml would render a full 5-star row each.
      const singleStarHtml = (filled, size) =>
        `<svg viewBox="0 0 24 24" fill="currentColor" class="${
          filled ? "text-dorahi-orange" : "text-gray-300"
        }" width="${size}" height="${size}" aria-hidden="true"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;

      const paintStars = () => {
        starsWrap.classList.add("items-center", "gap-1");
        starsWrap.innerHTML = "";
        for (let i = 1; i <= 5; i++) {
          const button = document.createElement("button");
          button.type = "button";
          button.setAttribute("aria-label", `Rate ${i} stars`);
          button.className =
            "relative cursor-pointer hover:scale-110 transition-transform rounded-sm";
          button.innerHTML = singleStarHtml(this.writeRating >= i, 18);
          button.addEventListener("click", () => {
            this.writeRating = i;
            paintStars();
            ratingLabel.textContent = RATING_LABELS[i];
            ratingLabel.classList.remove("hidden");
          });
          starsWrap.appendChild(button);
        }
      };
      paintStars();

      const paintPhotos = () => {
        previews.classList.toggle("hidden", this.writePhotos.length === 0);
        previews.innerHTML = "";
        this.writePhotos.forEach((photo, index) => {
          const wrap = document.createElement("div");
          wrap.className = "relative";
          wrap.innerHTML = `
            <span class="w-14 h-14 overflow-hidden block relative rounded-[6px] rounded-squircle border border-[#D1D5DB]">
              <img src="${photo.url}" alt="Photo ${index + 1}" class="absolute inset-0 w-full h-full object-cover">
            </span>
            <button type="button" class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md z-10">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>`;
          wrap.querySelector("button").addEventListener("click", () => {
            URL.revokeObjectURL(photo.url);
            this.writePhotos.splice(index, 1);
            paintPhotos();
          });
          previews.appendChild(wrap);
        });
        addButton.classList.toggle("hidden", this.writePhotos.length >= 5);
        addLabel.textContent =
          this.writePhotos.length === 0
            ? "Add photos"
            : `Add more (${5 - this.writePhotos.length} left)`;
      };

      const showError = (message) => {
        errorEl.textContent = message;
        errorEl.classList.remove("hidden");
      };

      // write-review-modal.tsx Transition: backdrop fades, panel slides up
      // from the bottom on mobile / scale-fades on desktop.
      const backdrop = this.q("[data-write-review-backdrop]");
      const panel = this.q("[data-write-review-panel]");
      let closeTimer = null;

      const open = () => {
        if (closeTimer) clearTimeout(closeTimer);
        modal.classList.remove("hidden");
        document.body.style.overflow = "hidden";
        requestAnimationFrame(() => {
          backdrop.classList.replace("opacity-0", "opacity-100");
          panel.classList.remove(
            "translate-y-full",
            "md:scale-95",
            "md:opacity-0",
          );
        });
      };
      const close = () => {
        backdrop.classList.replace("opacity-100", "opacity-0");
        panel.classList.add("translate-y-full", "md:scale-95", "md:opacity-0");
        document.body.style.overflow = "";
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          modal.classList.add("hidden");
          form.classList.remove("hidden");
          this.q("[data-write-review-success]").classList.add("hidden");
          form.reset();
          this.writeRating = 0;
          paintStars();
          ratingLabel.classList.add("hidden");
          this.writePhotos.forEach((photo) => URL.revokeObjectURL(photo.url));
          this.writePhotos = [];
          paintPhotos();
          errorEl.classList.add("hidden");
        }, 300);
      };

      this.q("[data-write-review-open]").addEventListener("click", open);
      this.q("[data-write-review-close]").addEventListener("click", close);
      this.q("[data-write-review-backdrop]").addEventListener("click", close);

      addButton.addEventListener("click", () => fileInput.click());
      fileInput.addEventListener("change", () => {
        const files = [...(fileInput.files || [])].slice(
          0,
          5 - this.writePhotos.length,
        );
        for (const file of files) {
          if (!file.type.startsWith("image/")) {
            showError("Only image files are allowed");
            continue;
          }
          if (file.size > 5 * 1024 * 1024) {
            showError("Each image must be less than 5MB");
            continue;
          }
          this.writePhotos.push({ file, url: URL.createObjectURL(file) });
        }
        fileInput.value = "";
        paintPhotos();
      });

      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        errorEl.classList.add("hidden");

        const name = form.elements.name.value.trim();
        const email = form.elements.email.value.trim();
        const body = form.elements.body.value.trim();

        if (this.writeRating === 0) return showError("Please select a rating");
        if (!name) return showError("Please enter your name");
        if (!email) return showError("Please enter your email");
        if (!body) return showError("Please write your review");

        const submit = this.q("[data-write-review-submit]");
        submit.disabled = true;
        this.q("[data-write-review-submit-idle]").classList.add("hidden");
        const loading = this.q("[data-write-review-submit-loading]");
        loading.classList.remove("hidden");
        loading.classList.add("flex");

        try {
          // 1+2. Upload each photo straight to Judge.me's temp S3 bucket via
          // their presigned form (what the official widget does — the create
          // API only takes hosted URLs/keys, never raw files).
          const pictureKeys = [];
          for (const photo of this.writePhotos) {
            const key = await this.uploadReviewPhoto(photo.file);
            pictureKeys.push(key);
          }

          // 3. Create the review with the uploaded keys.
          const formData = new FormData();
          formData.append("api_token", this.dataset.apiToken);
          formData.append("shop_domain", this.dataset.shopDomain);
          formData.append("url", this.dataset.shopDomain);
          formData.append("platform", "shopify");
          formData.append("id", this.dataset.productId);
          formData.append("email", email);
          formData.append("name", name);
          formData.append("rating", String(this.writeRating));
          if (form.elements.title.value.trim())
            formData.append("title", form.elements.title.value.trim());
          formData.append("body", body);
          pictureKeys.forEach((key, index) =>
            formData.append(`picture_keys[${index}]`, key),
          );

          const response = await fetch(`${JUDGEME_SUBMIT_BASE}/reviews`, {
            method: "POST",
            body: formData,
          });
          if (!response.ok) {
            let message = "Failed to submit review.";
            try {
              const json = await response.json();
              message = json.message || json.error || message;
            } catch (error) {
              /* non-JSON error */
            }
            throw new Error(message);
          }

          form.classList.add("hidden");
          this.q("[data-write-review-success]").classList.remove("hidden");
          setTimeout(() => {
            close();
            this.fetchReviews();
          }, 2000);
        } catch (error) {
          showError(error.message || "Something went wrong");
        } finally {
          submit.disabled = false;
          this.q("[data-write-review-submit-idle]").classList.remove("hidden");
          loading.classList.add("hidden");
          loading.classList.remove("flex");
        }
      });
    }
  }

  if (!customElements.get("dorahi-reviews")) {
    customElements.define("dorahi-reviews", DorahiReviews);
  }
})();
